// Microsoft Project XML Parser
// Parses MSP XML export format into the shared P6Project interface
// Reference: https://learn.microsoft.com/en-us/office-project/xml-data-interchange/task-elements-and-xml-structure

import type {
  P6Project,
  P6Activity,
  P6Relationship,
  P6Calendar,
  P6WBS,
  P6ActivityType,
  P6ActivityStatus,
  P6RelationshipType,
} from './p6-parser';

// ── Helpers ──────────────────────────────────────────────────────────

function getChild(parent: Element, tagName: string): Element | null {
  for (let i = 0; i < parent.children.length; i++) {
    const child = parent.children[i];
    if (child.localName === tagName) return child;
  }
  return null;
}

function getText(parent: Element, tagName: string): string | null {
  const el = getChild(parent, tagName);
  return el?.textContent?.trim() || null;
}

function getInt(parent: Element, tagName: string, defaultVal = 0): number {
  const txt = getText(parent, tagName);
  if (txt === null) return defaultVal;
  const n = parseInt(txt, 10);
  return isNaN(n) ? defaultVal : n;
}

function getBool(parent: Element, tagName: string): boolean {
  return getText(parent, tagName) === '1' || getText(parent, tagName)?.toLowerCase() === 'true';
}

/**
 * Parse an ISO 8601 duration string (e.g. "PT48H0M0S", "PT5D", "P3DT8H0M0S")
 * into hours.
 */
function parseDurationToHours(iso: string | null): number {
  if (!iso) return 0;
  let hours = 0;
  // Match days, hours, minutes, seconds
  const dayMatch = iso.match(/(\d+)D/);
  const hourMatch = iso.match(/(\d+)H/);
  const minMatch = iso.match(/(\d+)M(?!S)/); // M not followed by S (to avoid matching MS)
  const secMatch = iso.match(/(\d+)S/);
  if (dayMatch) hours += parseInt(dayMatch[1]) * 8; // 8-hour work days
  if (hourMatch) hours += parseInt(hourMatch[1]);
  if (minMatch) hours += parseInt(minMatch[1]) / 60;
  if (secMatch) hours += parseInt(secMatch[1]) / 3600;
  return hours;
}

/**
 * Map MSP ConstraintType integer to P6-style constraint string.
 * MSP values: 0=ASAP, 1=ALAP, 2=FNET, 3=FNLT, 4=MSO, 5=MFO, 6=SNET, 7=SNLT
 */
function mapConstraintType(val: number | null): string | null {
  if (val === null || val === undefined) return null;
  switch (val) {
    case 0:
      return 'As Soon As Possible';
    case 1:
      return 'As Late As Possible';
    case 2:
      return 'Finish No Earlier Than';
    case 3:
      return 'Finish No Later Than';
    case 4:
      return 'Must Start On';
    case 5:
      return 'Must Finish On';
    case 6:
      return 'Start No Earlier Than';
    case 7:
      return 'Start No Later Than';
    default:
      return null;
  }
}

/**
 * Map MSP PredecessorLink Type integer to P6-style relationship string.
 * MSP values: 0=FF, 1=FS, 2=SF, 3=SS
 */
function mapRelType(val: number): P6RelationshipType {
  switch (val) {
    case 0:
      return 'Finish to Finish';
    case 1:
      return 'Finish to Start';
    case 2:
      return 'Start to Finish';
    case 3:
      return 'Start to Start';
    default:
      return 'Finish to Start';
  }
}

// ── Determine Activity Type & Status ─────────────────────────────────

function determineType(taskEl: Element): P6ActivityType {
  if (getBool(taskEl, 'Summary')) return 'WBS Summary';
  if (getBool(taskEl, 'Milestone')) {
    // Check if it's a start or finish milestone based on duration
    const dur = parseDurationToHours(getText(taskEl, 'Duration'));
    if (dur === 0) {
      // If it has predecessors it's likely a finish milestone, otherwise start
      const predLinks = taskEl.getElementsByTagName('PredecessorLink');
      return predLinks.length > 0 ? 'Finish Milestone' : 'Start Milestone';
    }
  }
  return 'Task Dependent';
}

function determineStatus(taskEl: Element): P6ActivityStatus {
  const pctComplete = getInt(taskEl, 'PercentComplete', 0);
  if (pctComplete >= 100) return 'Completed';
  if (pctComplete > 0) return 'In Progress';
  const actualStart = getText(taskEl, 'ActualStart');
  if (actualStart) return 'In Progress';
  return 'Not Started';
}

// ── Main Parser ──────────────────────────────────────────────────────

export function parseMSPXML(xmlString: string): P6Project {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlString, 'text/xml');

  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    throw new Error(`XML Parse Error: ${parseError.textContent}`);
  }

  // MS Project XML has a <Project> root (or it may have a namespace)
  const projectEl = doc.getElementsByTagName('Project')[0];
  if (!projectEl) {
    throw new Error(
      'No <Project> element found. Please ensure this is a valid Microsoft Project XML export.'
    );
  }

  // ── Project metadata ─────────────────────────────────────────────
  const projectName =
    getText(projectEl, 'Name') || getText(projectEl, 'Title') || 'Unknown Project';
  const dataDate = getText(projectEl, 'StatusDate') || getText(projectEl, 'CurrentDate') || '';
  const plannedStartDate = getText(projectEl, 'StartDate') || '';
  const scheduledFinishDate = getText(projectEl, 'FinishDate') || '';

  // ── Calendars ────────────────────────────────────────────────────
  const calendars = new Map<number, P6Calendar>();
  const calendarsEl = getChild(projectEl, 'Calendars');
  if (calendarsEl) {
    const calEls = calendarsEl.getElementsByTagName('Calendar');
    for (let i = 0; i < calEls.length; i++) {
      const calEl = calEls[i];
      // Only process direct children of Calendars
      if (calEl.parentElement !== calendarsEl) continue;
      const uid = getInt(calEl, 'UID');
      const name = getText(calEl, 'Name') || '';

      // Try to calculate hours per day from WeekDays
      let hoursPerDay = 8; // default
      const weekDays = calEl.getElementsByTagName('WeekDay');
      let totalWorkHours = 0;
      let workDayCount = 0;
      for (let j = 0; j < weekDays.length; j++) {
        const wd = weekDays[j];
        const dayType = getInt(wd, 'DayType');
        const dayWorking = getBool(wd, 'DayWorking');
        // DayType 1=Sunday ... 7=Saturday
        if (dayWorking && dayType >= 1 && dayType <= 7) {
          workDayCount++;
          const timePeriods = wd.getElementsByTagName('TimePeriod');
          let dayHours = 0;
          for (let k = 0; k < timePeriods.length; k++) {
            const fromTime = getText(timePeriods[k], 'FromTime');
            const toTime = getText(timePeriods[k], 'ToTime');
            if (fromTime && toTime) {
              const from = new Date(`2000-01-01T${fromTime.substring(fromTime.indexOf('T') + 1)}`);
              const to = new Date(`2000-01-01T${toTime.substring(toTime.indexOf('T') + 1)}`);
              dayHours += (to.getTime() - from.getTime()) / (1000 * 60 * 60);
            }
          }
          if (dayHours > 0) totalWorkHours += dayHours;
        }
      }
      if (workDayCount > 0 && totalWorkHours > 0) {
        hoursPerDay = totalWorkHours / workDayCount;
      }

      calendars.set(uid, { objectId: uid, name, hoursPerDay });
    }
  }
  if (calendars.size === 0) {
    calendars.set(0, { objectId: 0, name: 'Default', hoursPerDay: 8 });
  }

  // ── Tasks & Relationships ────────────────────────────────────────
  const activities: P6Activity[] = [];
  const relationships: P6Relationship[] = [];
  const wbs: P6WBS[] = [];
  let relIdCounter = 1;

  const tasksEl = getChild(projectEl, 'Tasks');
  if (tasksEl) {
    const taskEls = tasksEl.getElementsByTagName('Task');
    for (let i = 0; i < taskEls.length; i++) {
      const taskEl = taskEls[i];
      // Only direct children of Tasks
      if (taskEl.parentElement !== tasksEl) continue;

      const uid = getInt(taskEl, 'UID');
      const name = getText(taskEl, 'Name') || '';
      const outlineLevel = getInt(taskEl, 'OutlineLevel', 0);
      const isSummary = getBool(taskEl, 'Summary');
      const wbsCode = getText(taskEl, 'WBS') || '';

      // Skip the project summary task (UID 0, OutlineLevel 0)
      if (uid === 0 && outlineLevel === 0 && isSummary) continue;

      // Collect WBS entries from summary tasks
      if (isSummary) {
        wbs.push({
          objectId: uid,
          code: wbsCode,
          name,
        });
      }

      const type = determineType(taskEl);
      const status = determineStatus(taskEl);

      const constraintTypeInt =
        getText(taskEl, 'ConstraintType') !== null ? getInt(taskEl, 'ConstraintType', 0) : null;
      const constraintType = mapConstraintType(constraintTypeInt);
      // ASAP is the default / non-constraining
      const effectiveConstraint = constraintType === 'As Soon As Possible' ? null : constraintType;

      const calendarUID = getInt(taskEl, 'CalendarUID', -1);

      const activity: P6Activity = {
        objectId: uid,
        id: getText(taskEl, 'ID')?.toString() || uid.toString(),
        name,
        type,
        status,
        calendarObjectId: calendarUID >= 0 ? calendarUID : 0,
        plannedDuration: parseDurationToHours(getText(taskEl, 'Duration')),
        remainingDuration: parseDurationToHours(getText(taskEl, 'RemainingDuration')),
        atCompletionDuration: parseDurationToHours(getText(taskEl, 'Duration')), // MSP doesn't have separate at-completion
        earlyStartDate: getText(taskEl, 'EarlyStart'),
        earlyFinishDate: getText(taskEl, 'EarlyFinish'),
        lateStartDate: getText(taskEl, 'LateStart'),
        lateFinishDate: getText(taskEl, 'LateFinish'),
        primaryConstraintType: effectiveConstraint,
        primaryConstraintDate: getText(taskEl, 'ConstraintDate'),
        wbsObjectId: isSummary ? null : uid, // will be refined below
      };

      activities.push(activity);

      // Parse predecessor links into relationships
      const predLinks = taskEl.getElementsByTagName('PredecessorLink');
      for (let j = 0; j < predLinks.length; j++) {
        const linkEl = predLinks[j];
        const predUID = getInt(linkEl, 'PredecessorUID');
        const relType = getInt(linkEl, 'Type', 1); // default FS
        // LinkLag is in tenths of minutes, convert to hours
        const linkLag = getInt(linkEl, 'LinkLag', 0);
        const lagHours = linkLag / 600; // 10ths of minutes -> hours

        relationships.push({
          objectId: relIdCounter++,
          predecessorActivityObjectId: predUID,
          successorActivityObjectId: uid,
          type: mapRelType(relType),
          lag: lagHours,
        });
      }
    }
  }

  return {
    name: projectName,
    dataDate,
    plannedStartDate,
    scheduledFinishDate,
    calendars,
    activities,
    relationships,
    wbs,
  };
}

/**
 * Detect whether an XML string is a Microsoft Project XML file.
 * Looks for MSP-specific elements/attributes vs. P6 Primavera namespace.
 */
export function isMSProjectXML(xmlString: string): boolean {
  // Quick heuristic checks before full parse
  // MSP XML has <Project xmlns="http://schemas.microsoft.com/project">
  // or <Project> with <Tasks> containing <Task> with <UID>
  if (xmlString.includes('schemas.microsoft.com/project')) return true;
  if (xmlString.includes('xmlns.oracle.com/Primavera')) return false;
  // Check for MSP-specific elements
  if (xmlString.includes('<MinutesPerDay>') || xmlString.includes('<MinutesPerWeek>')) return true;
  if (xmlString.includes('<DaysPerMonth>') || xmlString.includes('<CalendarUID>')) return true;
  return false;
}
